import React, { useCallback, useState } from "react";
import { createRoot, Root } from "react-dom/client";
import Swal, { SweetAlertResult } from "sweetalert2";

import PinInput from "../components/pinInput";
import { useAppSelector } from "../app/hooks";
import { getIsDeletePinRequired } from "../features/appSettings/appSettingsSlice";
import { POST, setPendingDeletePin } from "../services/axios.service";

type ConfirmAction = (
  title: string,
  message: string,
  buttonText: string,
  isPinRequired?: boolean
) => Promise<boolean>;

type DeletePinFieldProps = {
  onChange: (value: string) => void;
};

const DeletePinField = ({ onChange }: DeletePinFieldProps) => {
  const [pin, setPin] = useState<string>("");

  const handleChange = (value: string) => {
    setPin(value);
    onChange(value);
    Swal.resetValidationMessage();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") Swal.clickConfirm();
  };

  return (
    <div onKeyDown={handleKeyDown} className="mt-5 flex justify-center">
      <PinInput
        value={pin}
        onChange={handleChange}
        autoFocus
        slotClassName="h-11 w-9"
      />
    </div>
  );
};

const useConfirmPopup = (): ConfirmAction => {
  const isDeletePinRequired = useAppSelector(getIsDeletePinRequired);

  const confirmAction = useCallback(
    async (
      title: string,
      message: string,
      buttonText: string,
      isPinRequired: boolean = false
    ): Promise<boolean> => {
      setPendingDeletePin(null);

      const needsPin = isPinRequired && isDeletePinRequired;

      let pin = "";
      let pinRoot: Root | null = null;
      let pinFieldKey = 0;

      const renderPinField = () => {
        pin = "";
        pinFieldKey += 1;
        pinRoot?.render(
          <DeletePinField
            key={pinFieldKey}
            onChange={(value) => (pin = value)}
          />
        );
      };

      const verifyPin = async () => {
        if (!/^\d{6}$/.test(pin)) {
          Swal.showValidationMessage("ડિલીટ પિન 6 અંકનો હોવો જોઈએ.");
          return false;
        }

        const response: any = await POST(
          "/v1/admin/settings/delete-pin/verify",
          { deletePin: pin }
        );

        if (response?.data?.status !== "success") {
          renderPinField();
          Swal.showValidationMessage(
            response?.data?.message ||
              "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો."
          );
          return false;
        }

        return pin;
      };

      const result: SweetAlertResult = await Swal.fire({
        title: title,
        text: message,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#123f67",
        cancelButtonColor: "#dc2625",
        confirmButtonText: buttonText,
        cancelButtonText: "Close",
        focusConfirm: !needsPin,
        showLoaderOnConfirm: needsPin,
        allowOutsideClick: () => !Swal.isLoading(),
        didOpen: needsPin
          ? () => {
              const pinContainer = document.createElement("div");
              Swal.getHtmlContainer()?.after(pinContainer);
              pinRoot = createRoot(pinContainer);
              renderPinField();
            }
          : undefined,
        didDestroy: () => pinRoot?.unmount(),
        preConfirm: needsPin ? verifyPin : undefined,
      });

      if (!result.isConfirmed) return false;

      if (needsPin) setPendingDeletePin(result.value as string);

      return true;
    },
    [isDeletePinRequired]
  );

  return confirmAction;
};

export default useConfirmPopup;
